import React from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  LinearProgress,
  Divider,
  Alert
} from '@mui/material';
import { FileText, ArrowRight, AlertCircle, FileCheck } from 'lucide-react';
import { format } from 'date-fns';
import { apiService } from '../services/api';

const ExpenseDashboardSection = ({ report, bankStatement, onReportCreated }) => {
  const navigate = useNavigate();

  // Get current date for calculations
  const today = new Date();

  // Calculate days remaining until end of month
  const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const daysRemaining = Math.floor((endOfMonth - today) / (1000 * 60 * 60 * 24));
  
  // Calculate progress through the month (for progress bar)
  const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
  const currentDay = today.getDate();
  const progressPercentage = (currentDay / daysInMonth) * 100;

  // Safely get the total amount with fallback to 0
  const totalAmount = (report && typeof report.total_amount === 'number') 
    ? report.total_amount 
    : 0;





  return (
    <>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              <FileText size={20} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
              Expense Report
            </Typography>
            <Chip 
              label={report?.status === 'submitted' ? 'Submitted' : 'Draft'} 
              color={report?.status === 'submitted' ? 'success' : 'default'}
              size="small"
            />
          </Box>

          <Divider sx={{ mb: 2 }} />

          {report ? (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Month: {format(new Date(report.year, report.month - 1), 'MMMM yyyy')}
                </Typography>
                {report.status === 'submitted' ? (
                  <Typography variant="body2" color="text.secondary">
                    Submitted on: {report.submitted_at ? format(new Date(report.submitted_at), 'MM/dd/yyyy') : 'N/A'}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Due in: {daysRemaining} days ({format(endOfMonth, 'MM/dd/yyyy')})
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  Total Amount: ${totalAmount.toFixed(2)}
                </Typography>
              </Box>

              {/* Bank Statement Status */}
              {report.status === 'draft' && (
                <Box sx={{ mt: 2, mb: 2 }}>
                  {bankStatement ? (
                    <Alert severity="success" icon={<FileCheck size={16} />} sx={{ mb: 1 }}>
                      Bank statement is available
                    </Alert>
                  ) : (
                    <Alert severity="warning" icon={<AlertCircle size={16} />} sx={{ mb: 1 }}>
                      Waiting for bank statement from service manager
                    </Alert>
                  )}
                </Box>
              )}

              {report.status !== 'submitted' && (
                <>
                  <Box sx={{ mt: 3, mb: 1 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={progressPercentage} 
                      sx={{ height: 8, borderRadius: 4 }}
                      color={daysRemaining < 7 ? "warning" : "primary"}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {Math.floor(progressPercentage)}% through the month
                  </Typography>
                </>
              )}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No expense report found for the current month.
            </Typography>
          )}

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            {/* Show Manage/View button only if report exists */}
            {report && (
              <Button
                component={RouterLink}
                to="/expense-report"
                endIcon={<ArrowRight size={16} />}
                variant="outlined"
                color="primary"
                sx={{ minWidth: '140px' }}
              >
                {report?.status === 'submitted' || report?.workflow_status !== 'draft' ? 'View Report' : 'Manage Expenses'}
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>


    </>
  );
};

export default ExpenseDashboardSection;